<script lang="ts" setup>
import { computed } from 'vue'

import GmeDisplayer from '@/stay-displayer/grouping/smr/GmeDisplayer.vue'
import WeekDaysPresence from '@/stay-displayer/smr/rhs/WeekDaysPresence.vue'
import { countRhsPresenceDays } from '@/stay-displayer/smr/utils'
import { formatCost } from '@/stay-displayer/utils'
import { getSingleDiagnosisTypes } from '@/store/modules/diagnosis/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { HospitalizationType, Rhs } from '@/store/modules/stay/smr/types'

const props = defineProps<{
  rhs: Rhs
  hospitalizationType: HospitalizationType
}>()

const dasList = computed(() => props.rhs.currentModeDiagnosisGroup.dasList.map((das) => das.associatedDiagnosis))
const medicalActs = computed(() => props.rhs.codedMedicalActs.filter((codedAct) => !codedAct.disabled).map((medcialAct) => medcialAct.associatedAct))
const codedRehabilitationActs = computed(() => props.rhs.codedRehabilitationActs.filter((rehabilitationAct) => !rehabilitationAct.disabled).map((rehabilitationAct) => rehabilitationAct.associatedAct))

const cost = computed(() => formatCost(props.rhs.currentModeDiagnosisGroup.groupingResult.value))
</script>

<template>
  <div class="card border-0 bg-light-grey py-1 mb-0">
    <div
      v-if="hospitalizationType !== HospitalizationType.COMPLETE"
      class="card-body"
    >
      <div class="row">
        <div class="col-12">
          <span class="fw-bold me-3">GMT</span>
          <span>{{ rhs.currentModeDiagnosisGroup.groupingResult.gmt ?? '-' }}</span>
        </div>
        <div class="col-12 card-title">
          <span>{{ cost }}</span>
        </div>
      </div>
      <gme-displayer :gme="rhs.currentModeDiagnosisGroup.groupingResult.gme" />
    </div>
    <div class="card-body border-top">
      <span class="fw-bold me-3">Total jours</span>
      <span>{{ countRhsPresenceDays(rhs) }}</span>
    </div>
  </div>
  <div class="p-2">
    <div class="row">
      <div class="col-5 fw-bold">
        Jours
      </div>
      <div class="col-7">
        <week-days-presence :rhs="rhs" />
      </div>
    </div>
    <div
      v-for="diagType in getSingleDiagnosisTypes(PmsiTypeEnum.SMR, rhs.endDate)"
      :key="diagType"
      class="row"
    >
      <div class="col-5 fw-bold">
        {{ diagType }}
      </div>
      <div
        v-tooltip="rhs.currentModeDiagnosisGroup[diagType.toLowerCase()]?.associatedDiagnosis?.description ?? null"
        class="col-7"
      >
        {{ rhs.currentModeDiagnosisGroup[diagType.toLowerCase()]?.associatedDiagnosis?.reference ?? '-' }}
      </div>
    </div>
    <div class="row">
      <div class="col-5 fw-bold">
        DAS
      </div>
      <div
        class="col-7"
      >
        <span
          v-for="(das, idx) in dasList"
          :key="das.reference"
          v-tooltip="das.description ?? null"
        >{{ (idx > 0 ? ', ' : '') + das.reference }}</span>
      </div>
    </div>
    <div class="row">
      <div class="col-5 fw-bold">
        Actes CCAM&nbsp;({{ medicalActs.length }})
      </div>
      <div
        class="col-7"
      >
        <span
          v-for="(medicalAct, idx) in medicalActs"
          :key="medicalAct.reference"
          v-tooltip="medicalAct.description ?? null"
        >{{ (idx > 0 ? ', ' : '') + medicalAct.reference }}</span>
      </div>
    </div>
    <div class="row">
      <div class="col-5 fw-bold">
        Actes CSAR&nbsp;({{ codedRehabilitationActs.length }})
      </div>
      <div
        class="col-7"
      >
        <span
          v-for="(act, idx) in codedRehabilitationActs"
          :key="act.reference"
          v-tooltip="act.description ?? null"
        >{{ (idx > 0 ? ', ' : '') + act.reference }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card.bg-light-grey {
  .border-top {
    border-color: white!important;
  }
}

.card-title {
  font-size: 2rem;
}
</style>
